/* eslint-disable react/prop-types */
import React, { useMemo, useState } from 'react';

import Analytics from 'arbitrip-common/client/analytics';

import { LABEL_AVG_ROOM_NIGHT, buildDealViews, priceLinesOf } from '../dealViewModel/dealViewModel';
import useBookDeal from '../dealViewModel/useBookDeal';
import {
    COLOR_BORDER,
    COLOR_MUTED,
    COLOR_PRIMARY,
    COLOR_SECONDARY,
    COLOR_TEXT,
    CollapsiblePanel,
    DealLines,
    DetailChips,
    LABEL_BOOK,
    PerkLines,
    PolicyMark,
    RoomImage,
    SupersededNote,
    WithoutCardNote,
    buttonPrimaryStyle,
    sheetStyle,
} from '../dealViewModel/DealViewParts.react';

import './DealTableView.scss';

/**
 * DEV PANEL — the "Choose Your Room" TABLE design (`table___expand.rooms-markup.html`) over the cards
 * `dealViewModel.js` builds. One `.dtv-room` row per card, one deal row per visible deal.
 */

const TITLE = 'Table view';
const HEADER_ACCOMMODATION = 'Accommodation type';
const HEADER_CHOICE = 'Your Choice';
const HEADER_PRICE = 'Price';
const LABEL_NO_CARDS = 'No cards to show';
const LABEL_SHOW_LESS = 'Show less';
const LABEL_SHOW_MORE_PREFIX = 'Show';
const LABEL_MORE_DEAL = 'more deal';
const LABEL_MORE_DEALS = 'more deals';
const MOBILE_VISIBLE_ROWS = 3;
const CLASS_ROW = 'dtv-drow';
const CLASS_ROW_HIDDEN_ON_MOBILE = 'dtv-drow dtv-mb-hide';
const IMAGE_HEIGHT = 150;

const styles = {
    table: {
        border: `1px solid ${COLOR_BORDER}`,
        borderRadius: '12px',
        overflow: 'hidden',
        background: COLOR_BORDER,
        display: 'flex',
        flexDirection: 'column',
        gap: '1px',
    },
    head: { display: 'flex', background: COLOR_PRIMARY, color: '#fff' },
    headAcc: {
        flex: '0 1 280px',
        minWidth: '180px',
        padding: '14px 16px',
        fontSize: '13px',
        lineHeight: '16px',
        fontWeight: 700,
    },
    headRest: { flex: 1, minWidth: 0, display: 'flex' },
    headChoice: {
        flex: '1 1 220px',
        minWidth: '160px',
        padding: '14px 16px',
        fontSize: '13px',
        lineHeight: '16px',
        fontWeight: 700,
        borderLeft: '1px solid rgba(255,255,255,0.25)',
    },
    headPrice: {
        flex: '0 1 340px',
        minWidth: '230px',
        padding: '14px 16px',
        fontSize: '13px',
        lineHeight: '16px',
        fontWeight: 700,
        borderLeft: '1px solid rgba(255,255,255,0.25)',
    },
    room: { display: 'flex', alignItems: 'stretch', background: COLOR_BORDER, gap: '1px' },
    acc: {
        flex: '0 1 280px',
        minWidth: '180px',
        background: '#fff',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    title: { fontSize: '18px', lineHeight: '24px', fontWeight: 700, color: COLOR_TEXT },
    subtitle: { fontSize: '13px', lineHeight: '16px', color: COLOR_MUTED, marginTop: '-8px' },
    deals: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px', background: COLOR_BORDER },
    drow: { flex: 1, display: 'flex', alignItems: 'stretch', gap: '1px', background: COLOR_BORDER },
    deal: {
        flex: '1 1 220px',
        minWidth: '160px',
        background: '#fff',
        padding: '20px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        justifyContent: 'flex-start',
    },
    price: {
        flex: '0 1 340px',
        minWidth: '230px',
        background: '#fff',
        padding: '20px 16px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        justifyContent: 'space-between',
    },
    priceLines: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '1px',
        alignItems: 'flex-start',
    },
    total: { fontSize: '21px', lineHeight: '26px', fontWeight: 700, color: COLOR_PRIMARY },
    nights: { fontSize: '13px', lineHeight: '16px', color: COLOR_TEXT },
    avgRow: { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' },
    avg: { fontSize: '13px', lineHeight: '16px', color: COLOR_SECONDARY, whiteSpace: 'nowrap' },
    cta: { ...buttonPrimaryStyle, flexShrink: 0, height: '42px', padding: '0 16px', whiteSpace: 'nowrap' },
    more: {
        display: 'none',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        height: '44px',
        border: `1px solid ${COLOR_PRIMARY}`,
        borderRadius: '8px',
        background: '#fff',
        fontFamily: 'inherit',
        fontSize: '15px',
        fontWeight: 700,
        color: COLOR_PRIMARY,
        cursor: 'pointer',
    },
    empty: { fontSize: '12px', color: COLOR_MUTED },
};

function showMoreLabel(hiddenCount) {
    return `${LABEL_SHOW_MORE_PREFIX} ${hiddenCount} ${hiddenCount === 1 ? LABEL_MORE_DEAL : LABEL_MORE_DEALS}`;
}

const TableDealRow = ({ deal, profile, hiddenOnMobile, onBook }) => {
    const lines = priceLinesOf(deal, profile);
    return (
        <div className={hiddenOnMobile ? CLASS_ROW_HIDDEN_ON_MOBILE : CLASS_ROW} style={styles.drow}>
            <div className="dtv-deal" style={styles.deal}>
                <DealLines deal={deal} />
            </div>
            <div className="dtv-price" style={styles.price}>
                <div style={styles.priceLines}>
                    <span style={styles.total}>{lines.total}</span>
                    <span style={styles.nights}>{lines.nightsLine}</span>
                    <div style={styles.avgRow}>
                        <span style={styles.avg}>
                            {lines.avg} {LABEL_AVG_ROOM_NIGHT}
                        </span>
                        <PolicyMark policy={lines.policy} />
                    </div>
                </div>
                <button type="button" className="dtv-cta" style={styles.cta} onClick={() => onBook(deal)}>
                    {LABEL_BOOK}
                </button>
            </div>
        </div>
    );
};

const TableRoomRow = ({ card, profile, onBook, onMoreDetails }) => {
    const [showAll, setShowAll] = useState(false);
    const { details } = card;
    const hiddenCount = card.deals.length - MOBILE_VISIBLE_ROWS;

    return (
        <div className="dtv-room" style={styles.room}>
            <div className="dtv-acc" style={styles.acc}>
                {details.hasDetails && <RoomImage details={details} title={card.title} height={IMAGE_HEIGHT} />}
                <div style={styles.title} title={card.cardId}>
                    {card.title}
                </div>
                {card.subtitleLine && <div style={styles.subtitle}>{card.subtitleLine}</div>}
                {details.hasDetails && <DetailChips details={details} />}
                {details.hasDetails && <PerkLines details={details} onMoreDetails={() => onMoreDetails(card)} />}
                <SupersededNote count={card.supersededCount} alternatives={card.alternativesKept} />
            </div>

            <div className="dtv-deals" style={styles.deals}>
                {card.deals.map((deal, index) => (
                    <TableDealRow
                        key={deal.id}
                        deal={deal}
                        profile={profile}
                        hiddenOnMobile={!showAll && index >= MOBILE_VISIBLE_ROWS}
                        onBook={onBook}
                    />
                ))}
                {hiddenCount > 0 && (
                    <button
                        type="button"
                        className="dtv-mb-more"
                        style={styles.more}
                        onClick={() => setShowAll(!showAll)}
                    >
                        {showAll ? LABEL_SHOW_LESS : showMoreLabel(hiddenCount)}
                    </button>
                )}
            </div>
        </div>
    );
};

const DealTableView = ({ deals, hotel, profile, reservationManipulation, onRoomDetails }) => {
    const { cards, withoutCardCount } = useMemo(() => buildDealViews(deals, hotel), [deals, hotel]);
    const bookDeal = useBookDeal({ hotel, profile, reservationManipulation });
    const openDetails = (card) => onRoomDetails(card.details.room, card.title, Analytics.actions.views.roomInfoDialog);

    return (
        <CollapsiblePanel title={TITLE} count={cards.length}>
            <div className="dtv-root" style={sheetStyle}>
                {cards.length === 0 ? (
                    <div style={styles.empty}>{LABEL_NO_CARDS}</div>
                ) : (
                    <div className="dtv-table" style={styles.table}>
                        <div className="dtv-head" style={styles.head}>
                            <div style={styles.headAcc}>{HEADER_ACCOMMODATION}</div>
                            <div style={styles.headRest}>
                                <div style={styles.headChoice}>{HEADER_CHOICE}</div>
                                <div style={styles.headPrice}>{HEADER_PRICE}</div>
                            </div>
                        </div>
                        {cards.map((card) => (
                            <TableRoomRow
                                key={card.cardId}
                                card={card}
                                profile={profile}
                                onBook={bookDeal}
                                onMoreDetails={openDetails}
                            />
                        ))}
                    </div>
                )}
                <WithoutCardNote count={withoutCardCount} />
            </div>
        </CollapsiblePanel>
    );
};

export default DealTableView;
